import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ArrowDownCircle,
  ArrowLeft,
  ArrowUpCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CreditCard,
  Droplets,
  FileDown,
  Filter,
  Info,
  ListChecks,
  Search
} from 'lucide-react';
import { financeAPI } from '../services/api';
import {
  formatLocalizedDateTime,
  getTransactionDisplayType,
  getWaterTypeLabel
} from '../utils/i18nFormatters';
import {
  formatManilaDateInput,
  getManilaCurrentMonthRange
} from '../utils/inspectionMonthView';

const LIMIT_OPTIONS = [50, 100, 200];
const SUMMARY_PERIOD_OPTIONS = ['day', 'month', 'year'];
const PREVIEW_LIMIT = 3;
const MACHINE_PAGE_SIZE = 10;

const formatCurrency = (value) => `₱${Number(value || 0).toLocaleString('en-PH')}`;
const formatNumber = (value, fractionDigits = 0) => Number(value || 0).toLocaleString('en-PH', {
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits
});
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const displayValue = (value) => (hasValue(value) ? value : '-');

const getPrepaymentCreditState = (item = {}) => {
  if (!['topup', 'TopUp'].includes(item.type)) return null;
  const creditStatus = String(item.credit_status || item.creditStatus || '').toLowerCase();
  const balanceBefore = Number(item.balanceBefore ?? item.balance_before ?? 0);
  const balanceAfter = Number(item.balanceAfter ?? item.balance_after ?? 0);
  return creditStatus === 'credited' || Boolean(item.credited_at || item.creditedAt) || balanceAfter > balanceBefore
    ? 'credited'
    : 'paidNotCredited';
};

const getLinkedFinanceFilters = (searchParams) => {
  const financeType = searchParams.get('financeType') || '';
  const scope = searchParams.get('scope') === 'year' ? 'year' : 'month';
  const year = Number(searchParams.get('year'));
  const month = Number(searchParams.get('month'));
  const hasYear = Number.isInteger(year) && year >= 2000 && year <= 2100;
  const hasMonth = Number.isInteger(month) && month >= 1 && month <= 12;
  let startDate = '';
  let endDate = '';

  if (hasYear && scope === 'year') {
    startDate = `${year}-01-01`;
    endDate = `${year}-12-31`;
  } else if (hasYear && hasMonth) {
    const paddedMonth = String(month).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();
    startDate = `${year}-${paddedMonth}-01`;
    endDate = `${year}-${paddedMonth}-${String(lastDay).padStart(2, '0')}`;
  }

  return {
    financeType,
    scope,
    initial: {
      type: financeType === 'prepayment' ? 'topup' : financeType === 'water' ? 'dispense' : '',
      status: financeType ? 'Completed' : '',
      startDate,
      endDate,
      summaryPeriod: scope
    }
  };
};

function getPublicCardNumber(item) {
  return displayValue(item.displayCardNumber || item.cardNumber || item.originCard?.cardNumber || item.rfid || item.hardwareRfid);
}

function getUserDisplayName(item, t) {
  if (hasValue(item.displayUserName)) return item.displayUserName;
  if (hasValue(item.user?.name)) return item.user.name;
  if (hasValue(item.user?.nickname)) return item.user.nickname;
  if (hasValue(item.userId?.name)) return item.userId.name;
  if (hasValue(item.userId?.id)) return `User #${item.userId.id}`;
  if (item.displayUserKind === 'PHYSICAL_CARD' || item.isStandalonePhysical) {
    return t('transactions.userKinds.physicalCard', { defaultValue: 'Physical card user' });
  }
  return t('transactions.userKinds.system', { defaultValue: 'System' });
}

function getUserDisplayPhone(item) {
  return displayValue(item.displayUserPhone || item.user?.phoneNumber || item.userId?.phoneNumber);
}

function SectionButton({ expanded, onClick, expandLabel, collapseLabel }) {
  const Icon = expanded ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className="admin-secondary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-black transition"
    >
      <Icon size={16} />
      <span className="break-words">{expanded ? collapseLabel : expandLabel}</span>
    </button>
  );
}

export default function FinanceTransactions() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const linkedFinance = useMemo(() => getLinkedFinanceFilters(searchParams), [searchParams]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [summary, setSummary] = useState(null);
  const [financeScope, setFinanceScope] = useState('');
  const [machineSummary, setMachineSummary] = useState([]);
  const [showAllMachines, setShowAllMachines] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [machinePage, setMachinePage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    type: linkedFinance.initial.type,
    status: linkedFinance.initial.status,
    balanceSource: '',
    startDate: linkedFinance.initial.startDate,
    endDate: linkedFinance.initial.endDate,
    summaryPeriod: linkedFinance.initial.summaryPeriod || 'day',
    limit: 50
  });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1, limit: 50 });

  const typeOptions = useMemo(() => [
    { value: '', label: t('transactions.filters.allTypes') },
    { value: 'topup', label: t('transactions.types.topup') },
    { value: 'dispense', label: t('transactions.types.dispense') },
    { value: 'withdrawal', label: t('transactions.types.withdrawal') },
    { value: 'refund', label: t('transactions.types.refund') }
  ], [t]);

  const statusOptions = useMemo(() => [
    { value: '', label: t('transactions.filters.allStatuses') },
    { value: 'Completed', label: t('transactions.status.Completed') },
    { value: 'Pending', label: t('transactions.status.Pending') },
    { value: 'Failed', label: t('transactions.status.Failed') },
    { value: 'Cancelled', label: t('transactions.status.Cancelled') }
  ], [t]);

  const balanceSourceOptions = useMemo(() => [
    { value: '', label: t('transactions.filters.allSources') },
    { value: 'APP_TOPUP', label: t('transactions.balanceSource.APP_TOPUP') },
    { value: 'PAYERMAX_TOPUP', label: t('transactions.balanceSource.PAYERMAX_TOPUP') },
    { value: 'HEADQUARTERS_PROVIDED', label: t('transactions.balanceSource.HEADQUARTERS_PROVIDED') },
    { value: 'SIGNUP_BONUS', label: t('transactions.balanceSource.SIGNUP_BONUS') },
    { value: 'KUD_INITIAL_CARD', label: t('transactions.balanceSource.KUD_INITIAL_CARD') },
    { value: 'NORMAL_CARD', label: t('transactions.balanceSource.NORMAL_CARD') }
  ], [t]);

  const requestParams = useMemo(() => ({
    page,
    limit: filters.limit,
    search: filters.search || undefined,
    type: filters.type || undefined,
    status: filters.status || undefined,
    balanceSource: filters.balanceSource || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    summaryPeriod: filters.summaryPeriod || 'day',
    financeType: linkedFinance.financeType || undefined
  }), [filters, linkedFinance.financeType, page]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError('');
    setErrorCode('');
    try {
      const res = await financeAPI.getTransactions(requestParams);
      if (res.data?.success) {
        setTransactions(Array.isArray(res.data.data) ? res.data.data : []);
        setSummary(res.data.summary || null);
        setFinanceScope(res.data.meta?.financeScope || '');
        setMachineSummary(Array.isArray(res.data.machineSummary) ? res.data.machineSummary : []);
        setPagination(res.data.pagination || {
          total: res.data.total || 0,
          totalPages: res.data.pages || 1,
          currentPage: res.data.currentPage || page,
          limit: filters.limit
        });
      }
    } catch (err) {
      const responseCode = err.response?.data?.code || '';
      const scopeUnavailable = responseCode === 'DATA_SCOPE_NOT_READY';
      if (scopeUnavailable) {
        console.warn('Transaction totals stopped because the production data scope is not ready.');
      } else {
        console.error('Failed to load transactions:', err);
      }
      setTransactions([]);
      setSummary(null);
      setFinanceScope('');
      setMachineSummary([]);
      setPagination({ total: 0, totalPages: 1, currentPage: 1, limit: filters.limit });
      setErrorCode(responseCode);
      setError(scopeUnavailable
        ? t('transactions.errors.dataScopeNotReady')
        : (err.response?.data?.message || err.message || t('transactions.errors.load')));
    } finally {
      setLoading(false);
    }
  }, [filters.limit, page, requestParams, t]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    setMachinePage(1);
  }, [filters.summaryPeriod, machineSummary.length]);

  const updateFilter = (key, value) => {
    setPage(1);
    setMachinePage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    const end = formatManilaDateInput(today);
    let start = '';
    let finish = end;

    if (preset === 'today') {
      start = end;
    } else if (preset === 'yesterday') {
      start = formatManilaDateInput(addDays(today, -1));
      finish = start;
    } else if (preset === 'week') {
      start = formatManilaDateInput(addDays(today, -6));
    } else if (preset === 'month') {
      start = getManilaCurrentMonthRange(today).startDate;
    } else {
      finish = '';
    }

    setPage(1);
    setMachinePage(1);
    setFilters((prev) => ({ ...prev, startDate: start, endDate: finish }));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await financeAPI.exportTransactions({ ...requestParams, lang: i18n.language });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ATMWater_Finance_Transactions_${formatManilaDateInput()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export transactions:', err);
      alert(t('transactions.errors.export'));
    } finally {
      setExporting(false);
    }
  };

  const exportSummaryCsv = async () => {
    setExporting(true);
    try {
      const res = await financeAPI.exportTransactionSummary({ ...requestParams, lang: i18n.language });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ATMWater_Finance_Summary_${filters.summaryPeriod}_${formatManilaDateInput()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export transaction summary:', err);
      alert(t('transactions.errors.export'));
    } finally {
      setExporting(false);
    }
  };

  const getAmountClass = (type) => {
    if (['TopUp', 'topup', 'ReferralReward', 'referral_reward', 'ProfitShare'].includes(type)) {
      return 'text-[var(--admin-success)]';
    }
    return 'text-[var(--admin-danger)]';
  };

  const getTypeIcon = (type) => {
    if (['TopUp', 'topup', 'ReferralReward', 'referral_reward', 'ProfitShare'].includes(type)) {
      return <ArrowUpCircle size={16} />;
    }
    return <ArrowDownCircle size={16} />;
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const dataScopeUnavailable = errorCode === 'DATA_SCOPE_NOT_READY';
  const temporaryProductionIsolation = financeScope === 'temporary-live-production-isolation-payermax-only-topups';
  const machineTotalPages = Math.max(1, Math.ceil(machineSummary.length / MACHINE_PAGE_SIZE));
  const displayedMachineSummary = showAllMachines
    ? machineSummary.slice((machinePage - 1) * MACHINE_PAGE_SIZE, machinePage * MACHINE_PAGE_SIZE)
    : machineSummary.slice(0, PREVIEW_LIMIT);
  const displayedTransactions = showAllRecords ? transactions : transactions.slice(0, PREVIEW_LIMIT);

  const statCards = useMemo(() => [
    {
      key: 'sales',
      icon: Droplets,
      label: t('transactions.stats.salesAmount'),
      hint: t('transactions.stats.salesAmountHint', { defaultValue: 'Paid water revenue only. It uses completed transaction amounts, never water volume multiplied by price; gift-funded water is excluded.' }),
      value: dataScopeUnavailable ? '—' : formatCurrency(summary?.salesAmount || 0),
      accent: 'border-[var(--admin-accent)]'
    },
    {
      key: 'topup',
      icon: CreditCard,
      label: t('transactions.stats.successfulTopupAmount'),
      hint: temporaryProductionIsolation
        ? t('finance.productionIsolation.payermaxOnly')
        : t('transactions.stats.successfulTopupAmountHint', { defaultValue: 'Completed App or PayerMax top-up amount, used to check prepaid balance inflow.' }),
      value: dataScopeUnavailable ? '—' : formatCurrency(summary?.successfulTopupAmount || 0),
      accent: 'border-[var(--admin-success)]'
    },
    {
      key: 'volume',
      icon: ListChecks,
      label: t('transactions.stats.successfulWaterVolume'),
      hint: t('transactions.stats.successfulWaterVolumeHint', { defaultValue: 'All completed delivered-water volume in liters, including water funded by a gift balance.' }),
      value: dataScopeUnavailable ? '—' : `${formatNumber(summary?.successfulWaterVolume || 0, 2)} L`,
      accent: 'border-[var(--admin-primary)]'
    }
  ], [dataScopeUnavailable, summary, t, temporaryProductionIsolation]);

  const toggleRecordsMode = () => {
    if (showAllRecords) setPage(1);
    setShowAllRecords((prev) => !prev);
  };

  const renderMachineRows = () => {
    if (loading && machineSummary.length === 0) {
      return <tr><td colSpan="6" className="p-8 text-center text-[var(--admin-text-muted)]">{t('finance.transactions.table.loading')}</td></tr>;
    }
    if (displayedMachineSummary.length === 0) {
      return <tr><td colSpan="6" className="p-8 text-center text-[var(--admin-text-muted)]">{t('finance.transactions.table.noTransactions')}</td></tr>;
    }
    return displayedMachineSummary.map((row, index) => (
      <tr key={`${row.period}-${row.deviceId}-${index}`} className="border-t border-[var(--admin-border-subtle)] hover:bg-[var(--admin-surface-soft)]">
        <td className="p-4 font-mono text-xs text-[var(--admin-text-muted)]">{displayValue(row.period)}</td>
        <td className="p-4">
          <p className="break-all font-mono text-sm font-black text-[var(--admin-text-primary)]">{displayValue(row.deviceId)}</p>
          <p className="mt-1 break-words text-xs text-[var(--admin-text-muted)]">{displayValue(row.deviceName)}</p>
        </td>
        <td className="p-4 min-w-48 break-words text-sm text-[var(--admin-text-secondary)]">{displayValue(row.location)}</td>
        <td className="p-4 text-right font-mono text-sm font-black text-[var(--admin-success)]">{formatCurrency(row.salesAmount)}</td>
        <td className="p-4 text-right font-mono text-sm text-[var(--admin-text-primary)]">{formatNumber(row.waterVolume, 2)} L</td>
        <td className="p-4 text-right font-mono text-sm text-[var(--admin-text-primary)]">{formatNumber(row.transactionCount)}</td>
      </tr>
    ));
  };

  const renderTransactionRows = () => {
    if (loading && transactions.length === 0) {
      return <tr><td colSpan="8" className="p-10 text-center text-[var(--admin-text-muted)]">{t('finance.transactions.table.loading')}</td></tr>;
    }
    if (displayedTransactions.length === 0) {
      return <tr><td colSpan="8" className="p-10 text-center text-[var(--admin-text-muted)]">{t('finance.transactions.table.noTransactions')}</td></tr>;
    }
    return displayedTransactions.map((item) => (
      <tr key={item.id || item._id} className="border-t border-[var(--admin-border-subtle)] transition-colors hover:bg-[var(--admin-surface-soft)]">
        <td className="p-4 font-mono text-xs text-[var(--admin-text-muted)] whitespace-nowrap">
          {formatLocalizedDateTime(item.completedAt || item.createdAt, i18n.language, { second: '2-digit' })}
        </td>
        <td className="p-4 min-w-44">
          <p className="break-words text-sm font-black text-[var(--admin-text-primary)]">{getUserDisplayName(item, t)}</p>
          <p className="mt-1 break-all font-mono text-xs text-[var(--admin-text-muted)]">{getUserDisplayPhone(item)}</p>
        </td>
        <td className="p-4">
          <span className="inline-flex max-w-52 items-center gap-2 rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] px-2.5 py-1 text-xs font-black text-[var(--admin-text-secondary)]">
            {getTypeIcon(item.type)}
            <span className="break-words">{getTransactionDisplayType(t, item)}</span>
          </span>
        </td>
        <td className="p-4">
          <span className={`inline-flex max-w-48 rounded-lg px-2.5 py-1 text-xs font-black ${item.isKudInitialCard ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}`}>
            <span className="break-words">{t(`transactions.balanceSource.${item.balanceSource}`, { defaultValue: item.balanceSource || '-' })}</span>
          </span>
        </td>
        <td className={`p-4 font-mono text-sm font-black ${getAmountClass(item.type)}`}>
          {['WaterPurchase', 'dispense', 'Withdrawal', 'withdrawal'].includes(item.type) ? '-' : '+'}
          {formatCurrency(item.amount)}
        </td>
        <td className="p-4 min-w-44 text-xs">
          <p className="break-all font-mono font-black text-[var(--admin-text-primary)]">{displayValue(item.deviceId || item.unit?.deviceId)}</p>
          <p className="mt-1 break-all font-mono text-[var(--admin-text-muted)]">{getPublicCardNumber(item)}</p>
        </td>
        <td className="max-w-sm p-4 text-xs text-[var(--admin-text-muted)]">
          <div className="line-clamp-2 break-words leading-5">{displayValue(item.description)}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {item.waterType && <span className="rounded-lg bg-cyan-100 px-2 py-1 text-cyan-800">{getWaterTypeLabel(t, item.waterType)}</span>}
            {item.volume && <span className="rounded-lg bg-[var(--admin-surface-soft)] px-2 py-1 text-[var(--admin-text-primary)]">{Number(item.volume).toLocaleString('en-PH')} L</span>}
            {item.excludeFromFinanceRevenue && <span className="rounded-lg bg-amber-100 px-2 py-1 text-amber-800">{t('transactions.badges.excludeFinanceRevenue')}</span>}
            {item.excludeFromTierThreshold && <span className="rounded-lg bg-purple-100 px-2 py-1 text-purple-800">{t('transactions.badges.excludeTierThreshold')}</span>}
            {item.isHeadquartersProvided && <span className="rounded-lg bg-amber-100 px-2 py-1 text-amber-800">{t('transactions.paymentProvider.headquartersProvided')}</span>}
            {getPrepaymentCreditState(item) && (
              <span className={`rounded-lg px-2 py-1 font-black ${getPrepaymentCreditState(item) === 'credited' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                {t(`finance.transactions.creditStatus.${getPrepaymentCreditState(item)}`)}
              </span>
            )}
          </div>
        </td>
        <td className="p-4 text-right">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-black ${
            item.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : item.status === 'Failed' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {item.status === 'Completed' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {t(`transactions.status.${item.status}`, { defaultValue: item.status })}
          </span>
        </td>
      </tr>
    ));
  };

  const renderMachineCards = () => {
    if (loading && machineSummary.length === 0) {
      return <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.transactions.table.loading')}</div>;
    }
    if (displayedMachineSummary.length === 0) {
      return <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.transactions.table.noTransactions')}</div>;
    }
    return displayedMachineSummary.map((row, index) => (
      <article key={`${row.period}-${row.deviceId}-card-${index}`} className="rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="break-all font-mono text-sm font-black text-[var(--admin-text-primary)]">{displayValue(row.deviceId)}</p>
            <p className="mt-1 break-words text-sm text-[var(--admin-text-muted)]">{displayValue(row.deviceName)}</p>
          </div>
          <span className="shrink-0 rounded-lg bg-[var(--admin-surface)] px-2.5 py-1 font-mono text-xs font-bold text-[var(--admin-text-muted)]">{displayValue(row.period)}</span>
        </div>
        <p className="mt-3 break-words text-sm text-[var(--admin-text-secondary)]">{displayValue(row.location)}</p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('transactions.machineSummary.sales')}</p>
            <p className="mt-1 break-words font-black text-[var(--admin-success)]">{formatCurrency(row.salesAmount)}</p>
          </div>
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('transactions.machineSummary.volume')}</p>
            <p className="mt-1 font-black text-[var(--admin-text-primary)]">{formatNumber(row.waterVolume, 2)} L</p>
          </div>
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('transactions.machineSummary.count')}</p>
            <p className="mt-1 font-black text-[var(--admin-text-primary)]">{formatNumber(row.transactionCount)}</p>
          </div>
        </div>
      </article>
    ));
  };

  const renderTransactionCards = () => {
    if (loading && transactions.length === 0) {
      return <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.transactions.table.loading')}</div>;
    }
    if (displayedTransactions.length === 0) {
      return <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.transactions.table.noTransactions')}</div>;
    }
    return displayedTransactions.map((item) => (
      <article key={item.id || item._id} className="rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="break-words text-sm font-black text-[var(--admin-text-primary)]">{getUserDisplayName(item, t)}</p>
            <p className="mt-1 break-all font-mono text-xs text-[var(--admin-text-muted)]">{getUserDisplayPhone(item)}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-black ${item.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : item.status === 'Failed' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
            {t(`transactions.status.${item.status}`, { defaultValue: item.status })}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="inline-flex max-w-full items-center gap-2 rounded-lg bg-[var(--admin-surface)] px-2.5 py-1 text-xs font-black text-[var(--admin-text-secondary)]">
            {getTypeIcon(item.type)}
            <span className="break-words">{getTransactionDisplayType(t, item)}</span>
          </span>
          <span className="rounded-lg bg-cyan-100 px-2.5 py-1 text-xs font-black text-cyan-800">
            {t(`transactions.balanceSource.${item.balanceSource}`, { defaultValue: item.balanceSource || '-' })}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('finance.transactions.table.time')}</p>
            <p className="mt-1 font-mono text-[var(--admin-text-primary)]">{formatLocalizedDateTime(item.completedAt || item.createdAt, i18n.language, { second: '2-digit' })}</p>
          </div>
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('finance.transactions.table.amount')}</p>
            <p className={`mt-1 font-mono font-black ${getAmountClass(item.type)}`}>
              {['WaterPurchase', 'dispense', 'Withdrawal', 'withdrawal'].includes(item.type) ? '-' : '+'}
              {formatCurrency(item.amount)}
            </p>
          </div>
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('transactions.table.device')}</p>
            <p className="mt-1 break-all font-mono text-[var(--admin-text-primary)]">{displayValue(item.deviceId || item.unit?.deviceId)}</p>
          </div>
          <div>
            <p className="text-[var(--admin-text-muted)]">{t('transactions.table.cardNumber')}</p>
            <p className="mt-1 break-all font-mono text-[var(--admin-text-primary)]">{getPublicCardNumber(item)}</p>
          </div>
        </div>
        <p className="mt-3 break-words text-xs leading-5 text-[var(--admin-text-muted)]">{displayValue(item.description)}</p>
        {getPrepaymentCreditState(item) && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-xs font-black ${getPrepaymentCreditState(item) === 'credited' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
            {t(`finance.transactions.creditStatus.${getPrepaymentCreditState(item)}`)}
          </p>
        )}
      </article>
    ));
  };

  return (
    <div className="space-y-6 p-4 text-[var(--admin-text-primary)] sm:p-6 lg:p-8">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Link to="/finance" className="admin-secondary-button inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition">
            <ArrowLeft size={22} />
          </Link>
          <div className="min-w-0">
            <h2 className="break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)] sm:text-3xl">
              {t('finance.transactions.title')}
            </h2>
            <p className="mt-2 max-w-4xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">{t('transactions.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="admin-primary-button inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FileDown size={18} />
            {exporting ? t('transactions.exporting') : t('finance.transactions.exportDetail')}
          </button>
          <button
            type="button"
            onClick={exportSummaryCsv}
            disabled={exporting}
            className="admin-secondary-button inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FileDown size={18} />
            {t('transactions.exportSummary')}
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {error}
        </div>
      )}

      {linkedFinance.financeType === 'prepayment' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950" role="note">
          <p className="font-black">{t('finance.transactions.prepaymentNotice.title')}</p>
          <p className="mt-1">{t('finance.transactions.prepaymentNotice.description')}</p>
        </div>
      )}

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3" aria-label={t('transactions.stats.title', { defaultValue: 'Transaction definitions' })}>
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.key} className={`admin-card min-w-0 rounded-lg border-l-4 p-4 ${card.accent}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-words text-sm font-black text-[var(--admin-text-primary)]">{card.label}</p>
                  <p className="mt-2 break-words text-xs leading-5 text-[var(--admin-text-muted)]">{card.hint}</p>
                </div>
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--admin-surface-soft)] text-[var(--admin-accent)]" title={card.hint}>
                  <Icon size={20} />
                </span>
              </div>
              <p className="mt-4 break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)]">{card.value}</p>
            </article>
          );
        })}
      </section>

      <section className="admin-card rounded-lg p-4" aria-label={t('transactions.filters.title', { defaultValue: 'Filters' })}>
        <div className="mb-3 flex flex-wrap gap-2">
          {[
            ['today', t('transactions.presets.today')],
            ['yesterday', t('transactions.presets.yesterday')],
            ['week', t('transactions.presets.week')],
            ['month', t('transactions.presets.month')],
            ['custom', t('transactions.presets.custom')]
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => applyDatePreset(value)}
              className="admin-secondary-button min-h-9 rounded-lg px-3 py-1.5 text-xs font-black transition"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
          <div className="relative md:col-span-2 xl:col-span-4">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-text-muted)]" size={16} />
            <input
              type="text"
              placeholder={t('transactions.searchPlaceholder')}
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              className="admin-input min-h-11 w-full rounded-lg py-2.5 pl-10 pr-4 text-sm outline-none transition"
            />
          </div>
          <select value={filters.type} onChange={(event) => updateFilter('type', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm outline-none transition xl:col-span-2">
            {typeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm outline-none transition xl:col-span-2">
            {statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={filters.balanceSource} onChange={(event) => updateFilter('balanceSource', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm outline-none transition xl:col-span-2">
            {balanceSourceOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <label className="grid gap-1 text-xs font-semibold text-[var(--admin-text-muted)] xl:col-span-2">
            <span>{t('transactions.filters.startDate')}</span>
            <input aria-label={t('transactions.filters.startDate')} type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm font-normal outline-none transition" />
          </label>
          <label className="grid gap-1 text-xs font-semibold text-[var(--admin-text-muted)] xl:col-span-2">
            <span>{t('transactions.filters.endDate')}</span>
            <input aria-label={t('transactions.filters.endDate')} type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm font-normal outline-none transition" />
          </label>
          <select value={filters.summaryPeriod} onChange={(event) => updateFilter('summaryPeriod', event.target.value)} className="admin-input min-h-11 rounded-lg px-3 text-sm outline-none transition xl:col-span-2">
            {SUMMARY_PERIOD_OPTIONS.map((period) => (
              <option key={period} value={period}>
                {t(`transactions.summaryPeriod.${period}`)}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-3 flex items-start gap-2 break-words text-xs leading-5 text-[var(--admin-text-muted)]">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>{t('transactions.filterTimeHint')}</span>
        </p>
      </section>

      <section className="admin-card overflow-hidden rounded-lg" aria-label={t('transactions.machineSummary.title')}>
        <div className="flex flex-col gap-3 border-b border-[var(--admin-border-subtle)] p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('transactions.machineSummary.title')}</h3>
            <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">
              {showAllMachines
                ? t('transactions.machineSummary.allMode', { count: machineSummary.length, defaultValue: '{{count}} summary rows, paginated below.' })
                : t('transactions.previewRows', { count: PREVIEW_LIMIT, total: machineSummary.length, defaultValue: 'Showing first {{count}} of {{total}} rows.' })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="admin-muted-panel rounded-lg px-3 py-2 text-xs font-black text-[var(--admin-text-muted)]">
              {t(`transactions.summaryPeriod.${filters.summaryPeriod}`)}
            </span>
            {machineSummary.length > PREVIEW_LIMIT && (
              <SectionButton
                expanded={showAllMachines}
                onClick={() => setShowAllMachines((prev) => !prev)}
                expandLabel={t('transactions.machineSummary.viewAll', { defaultValue: 'View all machine summaries' })}
                collapseLabel={t('transactions.machineSummary.collapse', { defaultValue: 'Show first three' })}
              />
            )}
          </div>
        </div>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-[var(--admin-surface-soft)] text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('transactions.machineSummary.period')}</th>
                <th className="p-4">{t('transactions.machineSummary.device')}</th>
                <th className="p-4">{t('transactions.machineSummary.location')}</th>
                <th className="p-4 text-right">{t('transactions.machineSummary.sales')}</th>
                <th className="p-4 text-right">{t('transactions.machineSummary.volume')}</th>
                <th className="p-4 text-right">{t('transactions.machineSummary.count')}</th>
              </tr>
            </thead>
            <tbody>{renderMachineRows()}</tbody>
          </table>
        </div>
        <div className="grid gap-3 p-4 lg:hidden">{renderMachineCards()}</div>
        {showAllMachines && machineTotalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-[var(--admin-border-subtle)] px-4 py-3 text-sm text-[var(--admin-text-muted)]">
            <span>{t('transactions.machineSummary.pageInfo', { page: machinePage, totalPages: machineTotalPages, defaultValue: 'Page {{page}} / {{totalPages}}' })}</span>
            <div className="flex gap-2">
              <button disabled={machinePage <= 1 || loading} onClick={() => setMachinePage((p) => Math.max(1, p - 1))} className="admin-secondary-button rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft size={16} />
              </button>
              <button disabled={machinePage >= machineTotalPages || loading} onClick={() => setMachinePage((p) => Math.min(machineTotalPages, p + 1))} className="admin-secondary-button rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="admin-card overflow-hidden rounded-lg" aria-label={t('transactions.recordsTitle', { defaultValue: 'User transaction records' })}>
        <div className="flex flex-col gap-3 border-b border-[var(--admin-border-subtle)] p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 break-words text-base font-black text-[var(--admin-text-primary)]">
              <Filter size={17} className="shrink-0 text-[var(--admin-accent)]" />
              {t('transactions.recordsTitle', { defaultValue: 'User transaction records' })}
            </h3>
            <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">
              {showAllRecords
                ? t('transactions.recordsAllMode', { count: pagination.total || 0, defaultValue: '{{count}} records with pagination.' })
                : t('transactions.previewRows', { count: PREVIEW_LIMIT, total: pagination.total || transactions.length, defaultValue: 'Showing first {{count}} of {{total}} rows.' })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="admin-muted-panel rounded-lg px-3 py-2 text-xs font-black text-[var(--admin-text-muted)]">
              {t('transactions.totalRows', { count: pagination.total || 0 })}
            </span>
            {showAllRecords && (
              <select value={filters.limit} onChange={(event) => updateFilter('limit', Number(event.target.value))} className="admin-input min-h-10 rounded-lg px-3 text-sm outline-none">
                {LIMIT_OPTIONS.map((value) => <option key={value} value={value}>{t('transactions.perPage', { count: value })}</option>)}
              </select>
            )}
            {(pagination.total || transactions.length) > PREVIEW_LIMIT && (
              <SectionButton
                expanded={showAllRecords}
                onClick={toggleRecordsMode}
                expandLabel={t('transactions.viewAllRecords', { defaultValue: 'View all transaction records' })}
                collapseLabel={t('transactions.collapseRecords', { defaultValue: 'Show first three' })}
              />
            )}
          </div>
        </div>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="bg-[var(--admin-surface-soft)] text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('finance.transactions.table.time')}</th>
                <th className="p-4">{t('finance.transactions.table.user')}</th>
                <th className="p-4">{t('finance.transactions.table.type')}</th>
                <th className="p-4">{t('transactions.table.source')}</th>
                <th className="p-4">{t('finance.transactions.table.amount')}</th>
                <th className="p-4">{t('transactions.table.deviceCard')}</th>
                <th className="p-4">{t('finance.transactions.table.details')}</th>
                <th className="p-4 text-right">{t('finance.transactions.table.status')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">{renderTransactionRows()}</tbody>
          </table>
        </div>
        <div className="grid gap-3 p-4 lg:hidden">{renderTransactionCards()}</div>
        {showAllRecords && (
          <div className="flex items-center justify-between gap-3 border-t border-[var(--admin-border-subtle)] px-4 py-3 text-sm text-[var(--admin-text-muted)]">
            <span>{t('transactions.pageInfo', { page: pagination.currentPage || page, totalPages })}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="admin-secondary-button rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft size={16} />
              </button>
              <button disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="admin-secondary-button rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
